// Where everything around the race sits. Pure numbers, no three.js, so it can be unit tested.
// World units are metres. The track runs along +X from the start (x = 0) to the finish
// (x = TRACK_LENGTH). Lanes sit around z = 0; lane 0 is nearest the main grandstand (−z).
import { LANE_COUNT, LANE_WIDTH, TRACK_LENGTH } from '../../data/track';

/** Half the width of the six-lane running surface. */
export const TRACK_HALF_WIDTH = (LANE_COUNT * LANE_WIDTH) / 2;

/** z of every timber edging board: both outer edges and the five lane dividers. */
export function edgingZ(): number[] {
  return Array.from({ length: LANE_COUNT + 1 }, (_, i) => -TRACK_HALF_WIDTH + i * LANE_WIDTH);
}

/** The banner boards (hoardings) that close the course on both sides. */
export const HOARDING = { z: TRACK_HALF_WIDTH + 2.6, height: 1.05, panel: 4, from: -36, to: TRACK_LENGTH + 56 };

/** The course extends this far before the start and past the finish (run-out, podium). */
export const COURSE = { from: -40, to: TRACK_LENGTH + 64 };

/** The start stalls sit just behind the start line. */
export const GATES = { depth: 2.4, height: 1.25, headerY: 2.35 };

/** The finish gantry stands across the finish line. */
export const GANTRY = { x: TRACK_LENGTH, towerZ: TRACK_HALF_WIDTH + 1.7, beamY: 6.2 };

/**
 * The main grandstand runs along the −z side in repeated modules.
 * Rows climb away from the track; z values are negative (row 0 is the front row).
 */
export const STAND = {
  frontZ: -(HOARDING.z + 2.4),
  moduleLength: 24,
  rows: 9,
  rowDepth: 0.86,
  rowRise: 0.48,
  baseY: 0.9,
  roofFrontY: 8.6,
  roofBackY: 9.9,
  columnEvery: 6,
};

/** Start x of every grandstand module, covering the course from end to end. */
export function standModulesX(): number[] {
  const out: number[] = [];
  for (let x = COURSE.from + 16; x < COURSE.to - 8; x += STAND.moduleLength) out.push(x);
  return out;
}

/** Seat surface of a row: z of the seat's centre and y of its top. */
export function rowSeat(row: number): { z: number; y: number } {
  return {
    z: STAND.frontZ - 0.42 - row * STAND.rowDepth,
    y: STAND.baseY + row * STAND.rowRise,
  };
}

/** Back of the stand (z) and the height of its back wall top. */
export function standBack(): { z: number; y: number } {
  return { z: STAND.frontZ - STAND.rows * STAND.rowDepth - 0.5, y: STAND.baseY + STAND.rows * STAND.rowRise + 1.2 };
}

/** Seats per metre of row, for the spectator crowd. */
export const SEAT_SPACING = 0.62;

/** Seats per module row, and the share of seats taken by the 3D crowd. */
export const SEATS_PER_ROW = Math.floor(STAND.moduleLength / SEAT_SPACING);
export const OCCUPANCY = 0.88;

/**
 * How many grandstand modules the 3D crowd fills for a given number of spectators.
 * The other modules show painted crowd cards.
 */
export function crowdModules(count: number): number {
  if (count <= 0) return 0;
  return Math.ceil(count / (SEATS_PER_ROW * STAND.rows * OCCUPANCY));
}

/** First module of the 3D crowd window, centred on the focus x and kept inside the stand. */
export function crowdWindowStart(focusX: number, modules: number, total: number): number {
  if (modules >= total) return 0;
  const centre = Math.floor((focusX - standModulesX()[0]!) / STAND.moduleLength);
  return Math.max(0, Math.min(total - modules, centre - Math.floor(modules / 2)));
}

/** Where the podium stands: past the finish on the far (lake) side, facing the grandstand. */
export const PODIUM = { x: TRACK_LENGTH + 30, z: TRACK_HALF_WIDTH + 8, heights: [1.05, 0.75, 0.5] as const };

/** World positions of the 1st, 2nd and 3rd place tops (cats stand here). */
export function podiumSpots(): { x: number; y: number; z: number }[] {
  // Seen from the grandstand (looking +z), 2nd is on the viewer's left (+x) and 3rd on the right.
  const offsets = [0, 1.55, -1.55];
  return PODIUM.heights.map((h, i) => ({ x: PODIUM.x + offsets[i]!, y: h, z: PODIUM.z }));
}

/** Mulberry32: a small deterministic random generator so the world looks the same on every load. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface TreeSpot { x: number; z: number; scale: number; kind: 'pine' | 'birch'; rot: number }

/**
 * Trees around the course: a forest behind the grandstand, birch groves on the lake side
 * and clumps beyond both ends. Nothing is placed on the running surface, the apron,
 * the podium or the open view to the lake in the middle of the far side.
 */
export function treeSpots(seed = 7): TreeSpot[] {
  const r = rng(seed);
  const out: TreeSpot[] = [];
  const back = standBack().z;
  // Forest behind the grandstand.
  for (let i = 0; i < 1100; i++) {
    const x = COURSE.from - 120 + r() * (COURSE.to - COURSE.from + 240);
    const z = back - 6 - Math.pow(r(), 0.7) * 120;
    out.push({ x, z, scale: 0.8 + r() * 0.7, kind: r() < 0.78 ? 'pine' : 'birch', rot: r() * Math.PI * 2 });
  }
  // Lake side: birch groves with gaps so the lake shows through.
  for (let i = 0; i < 420; i++) {
    const x = COURSE.from - 60 + r() * (COURSE.to - COURSE.from + 120);
    const grove = Math.sin(x * 0.021) + 0.6 * Math.sin(x * 0.057 + 1.3);
    if (grove < 0.15) continue;
    const z = HOARDING.z + 14 + r() * 34;
    if (Math.abs(x - PODIUM.x) < 16 && z < PODIUM.z + 10) continue;
    out.push({ x, z, scale: 0.75 + r() * 0.6, kind: r() < 0.7 ? 'birch' : 'pine', rot: r() * Math.PI * 2 });
  }
  // Clumps beyond the start and the finish.
  for (const [cx, dir] of [[COURSE.from - 10, -1], [COURSE.to + 10, 1]] as const) {
    for (let i = 0; i < 160; i++) {
      const x = cx + dir * Math.pow(r(), 0.8) * 140;
      const z = -40 + r() * 100;
      out.push({ x, z, scale: 0.8 + r() * 0.7, kind: r() < 0.6 ? 'pine' : 'birch', rot: r() * Math.PI * 2 });
    }
  }
  return out;
}

/** The far shore of the lake starts here (z), so the lake reads behind the far-side groves. */
export const LAKE = { shoreZ: 70, y: -0.6 };
